import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import {
  Circle,
  HStack,
  Modal,
  ModalContent,
  ModalOverlay,
  Spacer,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  VStack,
  SimpleGrid,
  Divider,
  Box,
  InputGroup,
  InputLeftAddon,
  Progress,
  Checkbox,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import ReactSelect from 'react-select';

import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import * as Yup from 'yup';
import { isValid, parse } from 'date-fns';
import AppColors from '../../../../constants/AppColors';
import { AuditSheetContext } from '../../../../providers/AuditSheetProvider';
import { CloseIcon, OpenIcon } from '../../../../constants/IconData';
import { generateOptions } from '../../../../utils/responseColumn';
import { addArrayItem } from './helpers/dataActions';
import { convertToISODate, extractDynamicValues, extractDynamicModifierValues, extractDynamicICDValues, formatDynamicCPTValues, formatDynamicModifierValues, formatDynamicICDValues, resetDynamicKeys, buildCptIcdMap } from './helpers/dataUtils';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import RenderDynamicCPT from './helpers/RenderDynamicCPT';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import ProblemAddressedSelector from './helpers/ProblemAddressedSelector';
import DXOptionsSelector from './helpers/DXOptionsSelector';
import RiskComplicationsSelector from './helpers/RiskComplicationsSelector';
import ResponseSelector from './helpers/ResponseSelector';

const dots = keyframes`
  0%, 20% { content: ""; }
  40% { content: "."; }
  60% { content: ".."; }
  100% { content: "..."; }
`;

const fileTypeMap = {
  'application/CDFV2': 'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'application/vnd.ms-excel',
  'application/zip': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const FormViewer = ({ isOpen, onClose, currentFile, sheet, updateAuditSheet }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );
  const auditSheetContext = useContext(AuditSheetContext);
  const { currentUpload, selectedRowCol, setSelectedRowCol, providerOptions, industryCodes, iCDCodes, modifiers, auditsheetReference, responseAuditIssues } = auditSheetContext;

  const [view, setView] = useState('form');
  const [encounterNumber, setEncounterNumber] = useState('');
  const [encounterDate, setEncounterDate] = useState('');

  const [billedCPTs, setBilledCPTs] = useState([]);
  const [billedModifiers, setBilledModifiers] = useState([]);
  const [billedUnits, setBilledUnits] = useState([]);
  const [billedICDs, setBilledICDs] = useState([]);
  const [billedRVU, setBilledRVU] = useState('');
  const [billedDollarValue, setBilledDollarValue] = useState('');

  const [auditedCPTs, setAuditedCPTs] = useState([]);
  const [auditedModifiers, setAuditedModifiers] = useState([]);
  const [auditedUnits, setAuditedUnits] = useState([]);
  const [auditedICDs, setAuditedICDs] = useState([]);
  const [auditedRVU, setAuditedRVU] = useState('');
  const [auditedDollarValue, setAuditedDollarValue] = useState('');

  const [response, setResponse] = useState('');
  const [agree, setAgree] = useState(true);
  const [icdAgree, setICDAgree] = useState(true);

  const [notes, setNotes] = useState('');
  const [providerEducation, setProviderEducation] = useState('');
  const [numberComplexity, setNumberComplexity] = useState('');
  const [amountComplexity, setAmountComplexity] = useState('');
  const [riskComplications, setRiskComplications] = useState('');

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [isRowLoaded, setIsRowLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState([]);
  const [agreeAllResponse, setAgreeAllResponse] = useState(false);

  const [visibleBilledModifiers, setVisibleBilledModifiers] = useState([]);
  const [visibleAuditedModifiers, setVisibleAuditedModifiers] = useState([]);

  const refsForBilledICD = useRef([]);
  const refsForAuditedICD = useRef([]);
  const refsForBilledCPT = useRef([]);
  const refsForAuditedCPT = useRef([]);
  const refsForBilledModifier = useRef([]);
  const refsForAuditedModifier = useRef([]);

  const encounterNumberRef = useRef(null);
  const encounterDateRef = useRef(null);

  const [formErrors, setFormErrors] = useState({
    encounterNumber: '',
    encounterDate: '',
    billedCPTs: [],
    auditedCPTs: [],
  })

  const formSchema = Yup.object().shape({
    encounterNumber: Yup.string()
      .required('Encounter Number is required')
      .test('is-alphanumeric', 'Encounter Number must be alphanumeric', (value) => {
        if (value) {
          return /^[a-zA-Z0-9]+$/.test(value);
        }
        return true;
      }),
    encounterDate: Yup.string()
      .nullable()
      .required('Encounter Date is required')
      .test('is-valid-date', 'Encounter Date must be a valid date', (value) => {
        if (!value) return false;
        const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
        return isValid(parsedDate);
      }),
    billedCPTs: Yup.array()
      .of(
        Yup.string()
          .required('CPT cannot be blank')
          .test('is-not-empty', 'CPT cannot be blank', (value) => value.trim() !== '')
      ),
    auditedCPTs: Yup.array()
      .of(
        Yup.string()
          .required('CPT cannot be blank')
          .test('is-not-empty', 'CPT cannot be blank', (value) => value.trim() !== '')
      ),
  });

  useEffect(() => {
    setAgreeAllResponse(response === '-1');
  }, [response]);

  useEffect(() => {
    setVisibleBilledModifiers((prev) =>
      billedModifiers.map((mod, index) => {
        let prevCount = prev[index] ?? 1;

        let count = 1;
        if (mod?.secondary1) count++;
        if (mod?.secondary2) count++;

        return Math.max(prevCount, count);
      })
    );
  }, [billedModifiers]);

  useEffect(() => {
    setVisibleAuditedModifiers((prev) =>
      auditedModifiers.map((mod, index) => {
        let prevCount = prev[index] ?? 1;

        let count = 1;
        if (mod?.secondary1) count++;
        if (mod?.secondary2) count++;

        return Math.max(prevCount, count);
      })
    );
  }, [auditedModifiers]);

  useEffect(() => {
    if (selectedRowCol?.row !== undefined && sheet?.data?.[selectedRowCol.row]) {
      const rowData = sheet.data[selectedRowCol.row];
      setEncounterNumber(rowData.encounter_no || '');
      setEncounterDate(rowData.enc_dt ? convertToISODate(rowData.enc_dt) : "null");
      const matchingProvider = providerOptions.find((provider) => {
        if (!provider || !rowData) return false;
        const renderingId = rowData?.rendering?.id || null;
        const oldRenderingId = rowData?.old_rendering?.id || null;
        return provider.id === oldRenderingId || provider.id === renderingId;
      });
      setSelectedProvider(
        matchingProvider
          ? { value: matchingProvider.id, label: matchingProvider.value }
          : ""
      );

      const { cpts, units } = extractDynamicValues(rowData, 'srvcs_no');
      setBilledCPTs(cpts);
      setBilledUnits(units);

      const { cpts: acpts, units: aunits } = extractDynamicValues(rowData, 'audited_cpt');
      setAuditedCPTs(acpts);
      setAuditedUnits(aunits);

      handleAgreeDisagreeSelection(rowData);

      setBilledModifiers(extractDynamicModifierValues(rowData, 'billed_modifier', 'srvcs_no'))
      setAuditedModifiers(extractDynamicModifierValues(rowData, 'audited_modifier', 'audited_cpt'))

      setBilledICDs(extractDynamicICDValues(rowData, 'billed_icd', 'srvcs_no'))
      setAuditedICDs(extractDynamicICDValues(rowData, 'audited_icd', 'audited_cpt'))

      setIsRowLoaded(true);

      setBilledRVU(rowData.provider_rvu ?? '0.00');
      setBilledDollarValue(rowData.provider_dollar_value ?? '0.00');
      setAuditedRVU(rowData.audited_rvu ?? '0.00');
      setAuditedDollarValue(rowData.audited_dollar_value ?? '0.00');

      setResponse(rowData.response || '');
      setNotes(rowData.notes || '');
      setProviderEducation(rowData.provider_education || '');
      setRiskComplications(rowData.risk_complications || '');
      setNumberComplexity(rowData.number_complexity || '');
      setAmountComplexity(rowData.amount_complexity || '');

      setFormErrors({
        encounterNumber: '',
        encounterDate: '',
        billedCPTs: [],
        auditedCPTs: [],
      });

    } else {
      clearForm();
      setIsRowLoaded(false);
    }
  }, [selectedRowCol, sheet]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        isOpen &&
        (e.key === 'ArrowDown' ||
          e.key === 'ArrowUp' ||
          e.key === 'ArrowLeft' ||
          e.key === 'ArrowRight' ||
          e.key === 'Tab' ||
          e.key === 'Enter'
        )
      ) {
        e.stopPropagation(); // Stop event from propagating to parent
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isRowLoaded) {
      setIsRowLoaded(false);
      return;
    }
    calculateTotalsForType(billedCPTs, billedModifiers, billedUnits, setBilledRVU, setBilledDollarValue);
  }, [billedCPTs, billedModifiers, billedUnits]);

  useEffect(() => {
    if (isRowLoaded) {
      setIsRowLoaded(false);
      return;
    }
    calculateTotalsForType(auditedCPTs, auditedModifiers, auditedUnits, setAuditedRVU, setAuditedDollarValue);
  }, [auditedCPTs, auditedModifiers, auditedUnits]);

  const responseOptions = useMemo(() => {
    const rowData = sheet?.data?.[selectedRowCol?.row] || {};
    const newRowData = {
      ...rowData,
      ...formatDynamicCPTValues(billedCPTs, billedUnits, "srvcs_no"),
      ...formatDynamicCPTValues(auditedCPTs, auditedUnits, "audited_cpt"),
      ...formatDynamicICDValues(billedICDs, "billed_icd"),
      ...formatDynamicICDValues(auditedICDs, "audited_icd"),
      ...formatDynamicModifierValues(billedModifiers, "billed_modifier"),
      ...formatDynamicModifierValues(auditedModifiers, "audited_modifier"),
    };
    return generateOptions(newRowData, responseAuditIssues);
  }, [
    selectedRowCol,
    sheet,
    responseAuditIssues,
    billedCPTs,
    billedUnits,
    auditedCPTs,
    auditedUnits,
    billedICDs,
    auditedICDs,
    billedModifiers,
    auditedModifiers,
  ]);

  useEffect(() => {
    if (response && response !== '-1' && responseOptions.length > 0) {
        const selected = response.split(',').map(id => {
          const trimmed = id.trim();
          const exact = responseOptions.find(opt => String(opt.id) === trimmed);
          if (exact) return exact;
          const parts = trimmed.split(':');
          if (parts.length === 2) {
            const [baseId, code] = parts;
            const byCode = responseOptions.find(opt => {
              const p = String(opt.id).split(':');
              return p[0] === baseId && p[2]?.split('#')[0] === code;
            });
            if (byCode) return byCode;
          }
          // No exact or legacy match: the referenced code no longer exists
          // on the row. Drop it, matching the grid's resolveOptionName behavior.
          return null;
        }).filter(Boolean);
        const nextResponse = selected.map(opt => opt.id).join(',');
        setSelectedResponse(selected);
        if (nextResponse !== response) {
          setResponse(nextResponse);
        }
    } else {
        setSelectedResponse([]);
    }
  }, [response, responseOptions]);

  const renderFile = () => {
    const docs = [
      {
        uri: currentFile?.audited_chart_preview_url,
        fileType: fileTypeMap[currentFile?.audited_chart_mime_type] ?? currentFile?.audited_chart_mime_type,
        fileName: ''
      },
      {
        uri: currentFile?.preview_url,
        fileType: fileTypeMap[currentFile?.mime_type] ?? currentFile?.mime_type,
        fileName: ''
      }
    ].filter(doc => doc.uri);

    return <DocViewer pluginRenderers={DocViewerRenderers} documents={docs} style={{ minHeight: 500 }} />;
  };

  const clearForm = () => {
    setEncounterNumber('');
    setEncounterDate('');
    setBilledCPTs([]);
    setBilledModifiers([]);
    setBilledUnits([]);
    setBilledICDs([]);
    setAuditedCPTs([]);
    setAuditedModifiers([]);
    setAuditedUnits([]);
    setAuditedICDs([]);
    setVisibleBilledModifiers([]);
    setVisibleAuditedModifiers([]);
    setSelectedProvider(null);

    setResponse('');
    setNotes('');
    setProviderEducation('');
    setRiskComplications('');
    setNumberComplexity('');
    setAmountComplexity('');
  };

  const handleAgreeDisagreeSelection = (rowData) => {
    // --- CPT ---
    if (rowData?.agree === true && rowData?.disagree === false) {
      setAgree(true);
    } else if (rowData?.agree === false && rowData?.disagree === true) {
      setAgree(false);
    } else if (rowData?.agree == null || rowData?.disagree == null) {
      const srvcsKeys = Object.keys(rowData).filter(key => key.startsWith('srvcs_no'));
      const auditedKeys = Object.keys(rowData).filter(key => key.startsWith('audited_cpt'));

      const allSuffixes = Array.from(new Set([
        ...srvcsKeys.map(k => k.replace('srvcs_no', '')),
        ...auditedKeys.map(k => k.replace('audited_cpt', ''))
      ]));

      let foundAnyComparison = false;
      let isEqual = true;

      for (const suffix of allSuffixes) {
        const billedKey = `srvcs_no${suffix}`;
        const auditedKey = `audited_cpt${suffix}`;

        const billedValue = rowData[billedKey];
        const auditedValue = rowData[auditedKey];

        if (billedValue == null && auditedValue == null) continue;

        foundAnyComparison = true;

        if (billedValue !== auditedValue) {
          isEqual = false;
          break;
        }
      }

      setAgree(foundAnyComparison ? isEqual : true);
    }

    // --- ICD ---
    if (rowData?.icd_agree === true && rowData?.icd_disagree === false) {
      setICDAgree(true);
    } else if (rowData?.icd_agree === false && rowData?.icd_disagree === true) {
      setICDAgree(false);
    } else if (rowData?.icd_agree == null || rowData?.icd_disagree == null) {
      const billedKeys = Object.keys(rowData).filter(key => key.startsWith('billed_icd'));
      const auditedKeys = Object.keys(rowData).filter(key => key.startsWith('audited_icd'));

      const allSuffixes = Array.from(new Set([
        ...billedKeys.map(k => k.replace('billed_icd', '')),
        ...auditedKeys.map(k => k.replace('audited_icd', ''))
      ]));

      let foundAnyComparison = false;
      let isEqual = true;

      for (const suffix of allSuffixes) {
        const billedKey = `billed_icd${suffix}`;
        const auditedKey = `audited_icd${suffix}`;

        const billedValue = rowData[billedKey];
        const auditedValue = rowData[auditedKey];

        if (billedValue == null && auditedValue == null) continue;

        foundAnyComparison = true;

        if (billedValue !== auditedValue) {
          isEqual = false;
          break;
        }
      }

      setICDAgree(agree ? (foundAnyComparison ? isEqual : true) : false);
    }
  };

  const handleSubmit = async (shouldClose = true) => {
    try {
      setLoading(true);

      // check for custom errors
      const hasCustomErrors = Object.values(formErrors).some(errorGroup => {
        if (typeof errorGroup === "string") return !!errorGroup;
        if (typeof errorGroup === "object" && errorGroup !== null) {
          return Object.values(errorGroup).some(msg => !!msg);
        }
        return false;
      });

      if (hasCustomErrors) {
        // Scroll to the first visible error
        let firstErrorField = null;

        if (formErrors?.encounterNumber) {
          firstErrorField = encounterNumberRef;
        } else if (formErrors?.encounterDate) {
          firstErrorField = encounterDateRef;
        } else if (formErrors?.billedCPTs && Object.values(formErrors?.billedCPTs).some(err => !!err)) {
          const firstIdx = Object.keys(formErrors.billedCPTs).find(idx => formErrors.billedCPTs[idx]);
          if (firstIdx && refsForBilledCPT.current[firstIdx]) {
            firstErrorField = refsForBilledCPT.current[firstIdx];
          }
        } else if (formErrors?.auditedCPTs && Object.values(formErrors?.auditedCPTs).some(err => !!err)) {
          const firstIdx = Object.keys(formErrors.auditedCPTs).find(idx => formErrors.auditedCPTs[idx]);
          if (firstIdx && refsForAuditedCPT.current[firstIdx]) {
            firstErrorField = refsForAuditedCPT.current[firstIdx];
          }
        }

        if (firstErrorField) {
          if (firstErrorField.current) {
            firstErrorField.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstErrorField.current.focus();
          } else {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstErrorField.focus();
          }
        }
        return;
      }

      const formData = {
        encounterNumber,
        encounterDate,
        billedCPTs,
        auditedCPTs,
      };

      await formSchema.validate(formData, { abortEarly: false });

      const nextSheetData = [...(sheet?.data || [])];

      const resetSrvcsNo = resetDynamicKeys(nextSheetData[selectedRowCol.row], "srvcs_no");
      const resetBilledModifier = resetDynamicKeys(nextSheetData[selectedRowCol.row], "billed_modifier");
      const resetAuditedCpt = resetDynamicKeys(nextSheetData[selectedRowCol.row], "audited_cpt");
      const resetAuditedModifier = resetDynamicKeys(nextSheetData[selectedRowCol.row], "audited_modifier");
      const resetBilledIcd = resetDynamicKeys(nextSheetData[selectedRowCol.row], "billed_icd");
      const resetAuditedIcd = resetDynamicKeys(nextSheetData[selectedRowCol.row], "audited_icd");

      const billedcpt = formatDynamicCPTValues(billedCPTs, billedUnits, "srvcs_no");
      const billedmodifiers = formatDynamicModifierValues(billedModifiers, "billed_modifier");
      const auditedcpt = formatDynamicCPTValues(agree ? billedCPTs : auditedCPTs, agree ? billedUnits : auditedUnits, 'audited_cpt');
      const auditedmodifiers = formatDynamicModifierValues(agree ? billedModifiers : auditedModifiers, 'audited_modifier');

      const billedicd = formatDynamicICDValues(billedICDs, "billed_icd");
      const auditedicd = formatDynamicICDValues(icdAgree ? billedICDs : auditedICDs, "audited_icd");

      const billedCptIcdMap = buildCptIcdMap(billedCPTs, billedICDs, "srvcs_no", "billed_icd");
      const auditedCptIcdMap = buildCptIcdMap(icdAgree ? billedCPTs : auditedCPTs, icdAgree ? billedICDs : auditedICDs, 'audited_cpt', 'audited_icd');

      const finalBilledCPTValues = { ...resetSrvcsNo, ...billedcpt };
      const finalBilledModifierValues = { ...resetBilledModifier, ...billedmodifiers };
      const finalAuditedCPTValues = { ...resetAuditedCpt, ...auditedcpt };
      const finalAuditedModifierValues = { ...resetAuditedModifier, ...auditedmodifiers };
      const finalBilledICDValues = { ...resetBilledIcd, ...billedicd };
      const finalAuditedICDValues = { ...resetAuditedIcd, ...auditedicd };

      const finalAuditedRVU = agree ? billedRVU : auditedRVU;
      const finalAuditedDollarValue = agree ? billedDollarValue : auditedDollarValue;
      // Update the selected row if it exists, otherwise add a new row
      if (selectedRowCol?.row !== undefined && nextSheetData[selectedRowCol.row]) {
        nextSheetData[selectedRowCol.row] = {
          ...nextSheetData[selectedRowCol.row],
          file_name: currentUpload.upload_id,
          chart_id: currentUpload.id,
          encounter_no: encounterNumber,
          enc_dt: encounterDate,
          rendering: selectedProvider?.label || null,
          ...finalBilledCPTValues,
          ...finalAuditedCPTValues,
          ...finalBilledModifierValues,
          ...finalAuditedModifierValues,
          ...finalBilledICDValues,
          ...finalAuditedICDValues,
          provider_rvu: billedRVU,
          provider_dollar_value: billedDollarValue,
          audited_rvu: finalAuditedRVU,
          audited_dollar_value: finalAuditedDollarValue,
          agree: agree,
          disagree: !agree,
          icd_agree: icdAgree,
          icd_disagree: !icdAgree,
          response: response,
          notes: notes,
          provider_education: providerEducation,
          risk_complications: riskComplications,
          number_complexity: numberComplexity,
          amount_complexity: amountComplexity,
          billedCptIcdMap: billedCptIcdMap,
          auditedCptIcdMap: auditedCptIcdMap,
        };
      } else {
        nextSheetData.push({
          encounter_no: encounterNumber,
          rendering: selectedProvider?.label || null,
          enc_dt: encounterDate,
          ...billedcpt,
          ...auditedcpt,
          ...billedmodifiers,
          ...auditedmodifiers,
          ...billedicd,
          ...auditedicd,
          provider_rvu: billedRVU,
          provider_dollar_value: billedDollarValue,
          audited_rvu: finalAuditedRVU,
          audited_dollar_value: finalAuditedDollarValue,
          agree: agree,
          disagree: !agree,
          icd_agree: icdAgree,
          icd_disagree: !icdAgree,
          response: response,
          notes: notes,
          provider_education: providerEducation,
          risk_complications: riskComplications,
          number_complexity: numberComplexity,
          amount_complexity: amountComplexity,
          billedCptIcdMap: billedCptIcdMap,
          auditedCptIcdMap: auditedCptIcdMap,

          row_id: null,
          file_name: currentUpload.upload_id,
          chart_id: currentUpload.id,
        });
      }
      if (!shouldClose && encounterNumberRef && encounterNumberRef.current) {
        encounterNumberRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        encounterNumberRef.current.focus();
      }
      await updateAuditSheet({ ...sheet, data: nextSheetData });
      if (shouldClose) {
        onClose();
      }

    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errorDetails = {};
        let firstErrorField = null;

        error.inner.forEach((err) => {
          if (err.path === 'encounterNumber') {
            errorDetails.encounterNumber = err.message;
            if (!firstErrorField) firstErrorField = encounterNumberRef;
          }
          if (err.path === 'encounterDate') {
            errorDetails.encounterDate = err.message;
            if (!firstErrorField) firstErrorField = encounterDateRef;
          }
          if (err.path?.startsWith('billedCPTs')) {
            const index = err.path.match(/\[(\d+)\]/)?.[1];
            if (index !== undefined) {
              errorDetails.billedCPTs = errorDetails.billedCPTs || [];
              errorDetails.billedCPTs[index] = err.message;
              if (!firstErrorField && refsForBilledCPT.current[index]) {
                firstErrorField = refsForBilledCPT.current[index];
              }
            }
          }
          if (err.path?.startsWith('auditedCPTs')) {
            const index = err.path.match(/\[(\d+)\]/)?.[1];
            if (index !== undefined) {
              errorDetails.auditedCPTs = errorDetails.auditedCPTs || [];
              errorDetails.auditedCPTs[index] = err.message;
              if (!firstErrorField && refsForAuditedCPT.current[index]) {
                firstErrorField = refsForAuditedCPT.current[index];
              }
            }
          }
        });

        setFormErrors(errorDetails);
        if (firstErrorField) {
          if (firstErrorField.current) {
            firstErrorField.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstErrorField.current.focus();
          } else {
            firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstErrorField.focus();
          }
        }

        throw error;
      }
    }
    finally {
      setLoading(false);
    }
  };

  const handleChange = async (field, value) => {
    if (field === 'encounterNumber') {
      setEncounterNumber(value);
    } else if (field === 'encounterDate') {
      setEncounterDate(value);
    }

    // Reset the error for that specific field first
    setFormErrors((prevErrors) => ({ ...prevErrors, [field]: '' }));

    try {
      const fieldSchema = Yup.object().shape({
        [field]: formSchema.fields[field],
      });
      await fieldSchema.validate({ [field]: value });
      setFormErrors((prevErrors) => ({ ...prevErrors, [field]: '' }));
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        setFormErrors((prevErrors) => ({ ...prevErrors, [field]: error.message }));
      }
    }
  };


  const calculateTotalsForType = (cpts, modifiers, units, setRVU, setDollarValue) => {
    let totalRVU = 0;
    let totalDollarValue = 0;

    cpts.forEach((cpt, index) => {
      const modifier = modifiers[index]?.primary;
      const unit = units[index] || 1;

      const keyWithModifier = modifier ? `${cpt}-${modifier}` : cpt;
      const baseKey = cpt;

      const { rvu_value = 0, limiting_charge_amount = 0 } =
        industryCodes[keyWithModifier] || industryCodes[baseKey] || {};

      totalRVU += rvu_value * unit;
      totalDollarValue += limiting_charge_amount * unit;
    });

    setRVU(totalRVU.toFixed(2));
    setDollarValue(totalDollarValue.toFixed(2));
  };

  const renderDynamicFields = (
    label,
    cptArray = [],
    modifierArray = [],
    unitArray = [],
    setCPTs,
    setModifiers,
    setUnits,
    refsForCPT,
    refsForModifier,
    visibleModifiers,
    setVisibleModifiers,
    icdArray = [],
    setICDs,
    refsForICD
  ) => {
    const cptDisabled = label === "audited" && agree;
    const icdDisabled = label === "audited" && icdAgree;
    const errorKey = label === "billed" ? "billedCPTs" : "auditedCPTs";

    const handleCPTDragEnd = (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = parseInt(active.id);
      const newIndex = parseInt(over.id);

      setCPTs((prev) => arrayMove(prev, oldIndex, newIndex));
      setModifiers((prev) => arrayMove(prev, oldIndex, newIndex));
      setVisibleModifiers((prev) => arrayMove(prev, oldIndex, newIndex));
      setUnits((prev) => arrayMove(prev, oldIndex, newIndex));
      setICDs((prev) => arrayMove(prev, oldIndex, newIndex));

      if (label === 'billed' && agree && icdAgree) {
        setAuditedCPTs((prev) => arrayMove(prev, oldIndex, newIndex));
        setAuditedModifiers((prev) => arrayMove(prev, oldIndex, newIndex));
        setVisibleAuditedModifiers((prev) => arrayMove(prev, oldIndex, newIndex));
        setAuditedUnits((prev) => arrayMove(prev, oldIndex, newIndex));
        setAuditedICDs((prev) => arrayMove(prev, oldIndex, newIndex));
      }
      else if (label === 'billed' && agree && !icdAgree) {
        setAuditedCPTs((prev) => arrayMove(prev, oldIndex, newIndex));
        setAuditedModifiers((prev) => arrayMove(prev, oldIndex, newIndex));
        setVisibleAuditedModifiers((prev) => arrayMove(prev, oldIndex, newIndex));
        setAuditedUnits((prev) => arrayMove(prev, oldIndex, newIndex));
      }
    };

    return (
      <FormControl mt={3}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleCPTDragEnd}
          modifiers={[restrictToParentElement]}
        >
          <SortableContext
            items={Array.isArray(cptArray) ? cptArray.map((_, i) => i.toString()) : []}
            strategy={verticalListSortingStrategy}
          >
            {cptArray.map((cpt, cptIndex) => (
              <RenderDynamicCPT
                key={cptIndex}
                id={cptIndex}
                cptIndex={cptIndex}
                cpt={cpt}
                label={label}

                cptArray={cptArray}
                icdArray={icdArray}
                modifierArray={modifierArray}
                unitArray={unitArray}
                setCPTs={setCPTs}
                setICDs={setICDs}
                setModifiers={setModifiers}
                setUnits={setUnits}

                industryCodes={industryCodes}
                iCDCodes={iCDCodes}
                modifiers={modifiers}
                agree={agree}
                icdAgree={icdAgree}
                formSchema={formSchema}
                errorKey={errorKey}
                formErrors={formErrors}
                setFormErrors={setFormErrors}
                refsForCPT={refsForCPT}
                refsForICD={refsForICD}
                refsForModifier={refsForModifier}
                visibleModifiers={visibleModifiers}
                setVisibleModifiers={setVisibleModifiers}
                cptDisabled={cptDisabled}
                icdDisabled={icdDisabled}

                auditedCPTs={auditedCPTs}
                auditedICDs={auditedICDs}
                auditedModifiers={auditedModifiers}
                auditedUnits={auditedUnits}
                setAuditedCPTs={setAuditedCPTs}
                setAuditedICDs={setAuditedICDs}
                setAuditedModifiers={setAuditedModifiers}
                setAuditedUnits={setAuditedUnits}
                refsForAuditedCPT={refsForAuditedCPT}
                refsForAuditedICD={refsForAuditedICD}
                refsForAuditedModifier={refsForAuditedModifier}
                setVisibleAuditedModifiers={setVisibleAuditedModifiers}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Add CPT Row Button */}
        <Button
          mt={3}
          size="sm"
          colorScheme="green"
          isDisabled={cptDisabled}
          onClick={() => {
            addArrayItem(setCPTs, "", refsForCPT);
            addArrayItem(setModifiers, { primary: "", secondary1: "", secondary2: "" });
            addArrayItem(setUnits, 1);
            addArrayItem(setICDs, []);
            setVisibleModifiers((prev) => [...prev, 1]);
          }}
        >
          Add
        </Button>
      </FormControl >
    );
  };

  const handleSaveAndPrevious = async () => {
    setLoading(true);
    try {
      await handleSubmit(false);
    } catch (error) {
      return;
    }

    let newRowIndex;
    if (selectedRowCol.row > 0 && sheet.data[selectedRowCol.row - 1]?.id != null) {
      newRowIndex = selectedRowCol.row - 1;
    } else {
      const reversedData = [...sheet.data].reverse();
      const lastValidRowIndex = reversedData.findIndex(row => row.id !== null);
      if (lastValidRowIndex !== -1) {
        newRowIndex = sheet.data.length - lastValidRowIndex - 1;
      }
      else {
        newRowIndex = sheet.data.length - 1;
      }
    }
    if (newRowIndex !== undefined) {
      setSelectedRowCol({ row: newRowIndex });
      setTimeout(() => selectRowProgrammatically(newRowIndex), 0);
    }

    setLoading(false);
  };

  const handleSaveAndNext = async () => {
    setLoading(true);
    try {
      await handleSubmit(false);
    } catch (error) {
      return;
    }

    let newRowIndex;
    if (Number.isInteger(selectedRowCol.row) && sheet.data[selectedRowCol.row]?.id != null) {
      newRowIndex = selectedRowCol.row + 1;
    }
    else {
      const nextRowWithNullId = sheet.data.findIndex(row => row.id === null);
      if (nextRowWithNullId !== -1) {
        newRowIndex = nextRowWithNullId + 1;
      } else {
        newRowIndex = sheet.data.length + 1;
      }
    }
    if (newRowIndex !== undefined) {
      setSelectedRowCol({ row: newRowIndex });
      setTimeout(() => selectRowProgrammatically(newRowIndex), 0);
    }

    setLoading(false);
  };

  const handlePreviousWithoutSave = async () => {

    let newRowIndex;
    if (selectedRowCol.row > 0 && sheet.data[selectedRowCol.row - 1]?.id != null) {
      newRowIndex = selectedRowCol.row - 1;
    } else {
      const reversedData = [...sheet.data].reverse();
      const lastValidRowIndex = reversedData.findIndex(row => row.id !== null);
      if (lastValidRowIndex !== -1) {
        newRowIndex = sheet.data.length - lastValidRowIndex - 1;
      } else {
        newRowIndex = sheet.data.length - 1;
      }
    }

    if (newRowIndex !== undefined) {
      setSelectedRowCol({ row: newRowIndex });
      setTimeout(() => selectRowProgrammatically(newRowIndex), 0);
    }

    if (encounterNumberRef?.current) {
      setTimeout(() => {
        encounterNumberRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        encounterNumberRef.current.focus();
      }, 100);
    }
  };

  const handleNextWithoutSave = async () => {
    if (
      loading ||
      selectedRowCol.row === undefined ||
      selectedRowCol.row >= sheet.data.length ||
      sheet.data[selectedRowCol.row]?.id === null
    ) {
      return;
    }

    const newRowIndex = Math.min(sheet.data.length, selectedRowCol.row + 1);

    setSelectedRowCol({ row: newRowIndex });

    setTimeout(() => selectRowProgrammatically(newRowIndex), 0);

    if (encounterNumberRef?.current) {
      setTimeout(() => {
        encounterNumberRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        encounterNumberRef.current.focus();
      }, 100);
    }
  };


  const selectRowProgrammatically = (rowIndex) => {
    const hotInstance = auditsheetReference.hotRef?.hotInstance;

    if (hotInstance) {
      hotInstance.selectRows(rowIndex);
    }
  };

  const renderForm = () => (
    <VStack p={6} align="stretch">
      <Text fontSize="xl" fontWeight="bold" mb={3} >{(sheet?.data?.[selectedRowCol?.row]?.id == null || Object.keys(selectedRowCol).length === 0) ? 'Enter Encounter Details' : 'Edit Encounter Details'}</Text>
      <Divider />
      <SimpleGrid columns={3} gap={4} px="1%" pb={3}>
        <FormControl isInvalid={!!formErrors.encounterNumber}>
          <FormLabel>Encounter Number</FormLabel>
          <Input
            ref={encounterNumberRef}
            placeholder="Enter Encounter Number"
            value={encounterNumber}
            onChange={(e) => handleChange('encounterNumber', e.target.value)}
          />
          {formErrors.encounterNumber && (
            <Text color="red.500" fontSize="sm" mt={1}>
              {formErrors.encounterNumber}
            </Text>
          )}
        </FormControl>
        <FormControl isInvalid={!!formErrors.encounterDate}>
          <FormLabel>Encounter Date</FormLabel>
          <Input
            ref={encounterDateRef}
            type="date"
            value={encounterDate}
            onChange={(e) => handleChange('encounterDate', e.target.value)}
          />
          {formErrors.encounterDate && (
            <Text color="red.500" fontSize="sm" mt={1}>
              {formErrors.encounterDate}
            </Text>
          )}
        </FormControl>
        <FormControl>
          <FormLabel>Provider (Rendering)</FormLabel>
          <ReactSelect
            placeholder="Select Provider"
            options={providerOptions.map((provider) => ({
              value: provider.id,
              label: provider.value
            }))}
            value={selectedProvider}
            onChange={(selectedOption) => setSelectedProvider(selectedOption || null)}
            isSearchable
            noOptionsMessage={() => 'No Providers Found'}
          />
        </FormControl>
      </SimpleGrid>

      <Divider />
      <SimpleGrid columns={2} spacing={6} pb={3}>
        {/* Billed Details */}
        <Box>
          <Text fontSize="lg" fontWeight="bold" my={3} color="blue.600" textAlign="center">
            BILLED
          </Text>
          <Divider my={2} />
          <Box mx="2%">
            {renderDynamicFields(
              "billed",
              billedCPTs,
              billedModifiers,
              billedUnits,
              setBilledCPTs,
              setBilledModifiers,
              setBilledUnits,
              refsForBilledCPT,
              refsForBilledModifier,
              visibleBilledModifiers,
              setVisibleBilledModifiers,
              billedICDs,
              setBilledICDs,
              refsForBilledICD
            )}
            {(billedCPTs.length > 0 || billedICDs.some((arr) => arr.length > 0)) && (
              <SimpleGrid columns={2} spacing={4} my={4}>
                <FormControl>
                  <FormLabel>Billed RVU</FormLabel>
                  <Input value={billedRVU} onChange={(e) => setBilledRVU(e.target.value)} />
                </FormControl>
                <FormControl>
                  <FormLabel>Billed Dollar Value</FormLabel>
                  <InputGroup>
                    <InputLeftAddon children="$" />
                    <Input
                      value={billedDollarValue}
                      onChange={(e) => setBilledDollarValue(e.target.value)}
                      placeholder="Enter amount"
                    />
                  </InputGroup>
                </FormControl>
              </SimpleGrid>
            )}
          </Box>
        </Box>

        <Box borderLeft="1px solid" borderColor="gray.300" pl={4} >
          <Text fontSize="lg" fontWeight="bold" my={3} color="green.600" textAlign="center">
            AUDITED
          </Text>
          <Divider my={2} />
          <Box mx="2%">
            {renderDynamicFields(
              "audited",
              auditedCPTs,
              auditedModifiers,
              auditedUnits,
              setAuditedCPTs,
              setAuditedModifiers,
              setAuditedUnits,
              refsForAuditedCPT,
              refsForAuditedModifier,
              visibleAuditedModifiers,
              setVisibleAuditedModifiers,
              auditedICDs,
              setAuditedICDs,
              refsForAuditedICD
            )}
            {(auditedCPTs.length > 0 || auditedICDs.some((arr) => arr.length > 0)) && (
              <SimpleGrid columns={2} spacing={4} my={4}>
                <FormControl>
                  <FormLabel>Audited RVU</FormLabel>
                  <Input value={auditedRVU} onChange={(e) => setAuditedRVU(e.target.value)} disabled={agree} />
                </FormControl>
                <FormControl>
                  <FormLabel>Audited Dollar Value</FormLabel>
                  <InputGroup>
                    <InputLeftAddon children="$" />
                    <Input
                      value={auditedDollarValue}
                      onChange={(e) => setAuditedDollarValue(e.target.value)}
                      placeholder="Enter amount"
                      disabled={agree}
                    />
                  </InputGroup>
                </FormControl>
              </SimpleGrid>
            )}
          </Box>
        </Box>
      </SimpleGrid>

      {(billedCPTs.length > 0 || billedICDs.some((arr) => arr.length > 0) || auditedCPTs.length > 0 || auditedICDs.some((arr) => arr.length > 0)) && (
        <Box>
          <Divider />
          <SimpleGrid columns={2} spacing={6}>
            <Box my={5}
              mx={4}
              p={4}
              border="1px solid"
              borderColor="gray.300"
              borderRadius="lg"
              backgroundColor="#f9f9f9"
              boxShadow="md">
              <SimpleGrid columns={2} spacing={4} >
                <FormControl>
                  <HStack pl="4%">
                    <FormLabel fontSize="md" fontWeight="bold" color="gray.600" m={0}>CPT:</FormLabel>
                    <Button
                      size="md"
                      variant="outline"
                      colorScheme={agree ? 'green' : 'gray'}
                      onClick={() => {
                        if (!agree) {
                          setAuditedCPTs([...billedCPTs]);
                          setAuditedUnits([...billedUnits]);
                          setAuditedModifiers(billedModifiers.map((mods) => ({ ...mods })));
                        }
                        setAgree(true);
                        if (icdAgree) {
                          setAuditedICDs(billedICDs.map((icds) => [...icds])); // Prevents shared references
                        }
                      }}
                    >
                      Agree
                    </Button>
                    <Button
                      size="md"
                      variant="outline"
                      colorScheme={!agree ? 'red' : 'gray'}
                      onClick={() => {
                        setAgree(false);
                        if (icdAgree) setICDAgree(false); // Ensure ICD also becomes disagree if CPT is disagreed
                      }}
                    >
                      Disagree
                    </Button>
                  </HStack>
                </FormControl>
                <FormControl>
                  <HStack>
                    <FormLabel fontSize="md" fontWeight="bold" color="gray.600" m={0}>ICD:</FormLabel>
                    <Button
                      size="md"
                      variant="outline"
                      colorScheme={icdAgree ? 'green' : 'gray'}
                      onClick={() => {
                        if (!icdAgree) {
                          setAuditedICDs(billedICDs.map((icds) => [...icds])); // Prevents shared references
                        }
                        setICDAgree(true);

                        if (agree) {
                          setAuditedCPTs([...billedCPTs]);
                          setAuditedUnits([...billedUnits]);
                          setAuditedModifiers(billedModifiers.map((mods) => ({ ...mods })));
                        }
                      }}
                      isDisabled={!agree} // Disable ICD Agree if CPT is disagreed
                    >
                      Agree
                    </Button>
                    <Button
                      size="md"
                      variant="outline"
                      colorScheme={!icdAgree ? 'red' : 'gray'}
                      onClick={() => setICDAgree(false)}
                    >
                      Disagree
                    </Button>
                  </HStack>
                </FormControl>
              </SimpleGrid>

            </Box>
            <Box visibility="hidden" />
          </SimpleGrid>
        </Box>
      )
      }
      <Divider />
      <Box px="1%" pb={6}>
        <SimpleGrid columns={2} spacing={6} pt={4} >
          <ProblemAddressedSelector
            value={numberComplexity}
            onChange={(val) => setNumberComplexity(val)}
          />
          <DXOptionsSelector
            value={amountComplexity}
            onChange={(val) => setAmountComplexity(val)}
          />
        </SimpleGrid>
        <SimpleGrid columns={2} spacing={6} pt={4} >
          <RiskComplicationsSelector
            value={riskComplications}
            onChange={(val) => setRiskComplications(val)}
          />
          <FormControl>
            <FormLabel>Feedback to the provider</FormLabel>
            <Input value={providerEducation} onChange={(e) => setProviderEducation(e.target.value)} />
          </FormControl>
        </SimpleGrid>
        <FormControl pt={4}>
          <HStack spacing={4} alignItems="center" mb={2}>
            <FormLabel m={0}>Response</FormLabel>
            <Checkbox
              colorScheme="green"
              isChecked={agreeAllResponse}
              onChange={(e) => {
                setResponse(e.target.checked ? '-1' : '');
              }}
            >
              Agree Audits
            </Checkbox>
          </HStack>
          {!agreeAllResponse && (
            <ResponseSelector
              response={response}
              setResponse={setResponse}
              responseOptions={responseOptions}
              selectedResponse={selectedResponse}
              setSelectedResponse={setSelectedResponse}
            />
          )}
        </FormControl>
        <FormControl>
          <FormLabel pt={4} >Notes</FormLabel>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormControl>
      </Box>
      <Divider />

      <HStack justify="space-between" spacing={4} px="15%" pt={3}>
        <HStack spacing={2}>
          {/* Previous Button */}
          <Button
            colorScheme='gray'
            size="md"
            isDisabled={loading || selectedRowCol.row === 0 || sheet.data[0]?.id == null}
            onClick={handlePreviousWithoutSave}
          >
            &lt;
          </Button>
          {/* Save and Previous */}
          <Button
            colorScheme="teal"
            size="md"
            isDisabled={loading || selectedRowCol.row === 0 || sheet.data[0]?.id == null}
            onClick={handleSaveAndPrevious}
          >
            Save & Previous
          </Button>
        </HStack>
        <Box pr={2}>
          <Button colorScheme="blue" size="lg" isDisabled={loading} onClick={() => handleSubmit().catch(() => { /* Validation errors are handled, no need to show overlay */ })}>
            {(sheet?.data?.[selectedRowCol?.row]?.id == null || Object.keys(selectedRowCol).length === 0) ? 'Save' : 'Update'}
          </Button>
        </Box>
        {/* Save and Next */}
        <HStack spacing={2}>
          <Button
            colorScheme="teal"
            size="md"
            isDisabled={loading}
            onClick={handleSaveAndNext}
          >
            Save & Next
          </Button>
          {/* Next Without Save */}
          <Button
            colorScheme='gray'
            size="md"
            isDisabled={
              loading ||
              selectedRowCol.row == undefined ||
              selectedRowCol.row >= sheet.data.length ||
              sheet.data[selectedRowCol.row]?.id == null
            }
            onClick={handleNextWithoutSave}
          >
            &gt;
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );

  return (
    <Modal size={'xl'} onClose={onClose} isOpen={isOpen} scrollBehavior={'inside'}>
      <ModalOverlay bg="#001A41" style={{ opacity: 0.8 }} />
      <ModalContent
        style={{
          background: 'white',
          boxShadow: 'md',
          maxWidth: '90%',
          maxHeight: '85%',
          overflowY: 'auto',
          borderRadius: '8px',
          padding: '16px 16px 16px 16px'
        }}
      >
        <Box
          position="sticky"
          top="-4"
          bg="white"
          zIndex={1}
          boxShadow="sm"
          p={2}
        >
          {loading && (
            <Progress
              size="xs"
              isIndeterminate
              colorScheme="teal"
              position="absolute"
              top="0"
              left="0"
              right="0"
              borderTopRadius="md"
              zIndex="2"
            />
          )}
          <HStack>
            <Text
              fontSize="xl"
              fontWeight="bold"
              color="teal.600"
              maxWidth="20%"
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {currentUpload?.upload_id?.toUpperCase()}
            </Text>
            <Text fontSize="md" color="gray.500" mx={2}> | </Text>
            <Text fontSize="md" color="black" fontWeight="normal" maxWidth="40%" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentFile?.name.split('_').join(' ')}
            </Text>
            {currentFile?.mime_type === 'application/pdf' ? <Circle
              size="30px"
              bg={AppColors.gray}
              color={AppColors.gray}
              onClick={() => {
                window.open(currentFile?.preview_url, '_blank');
              }}
              style={{ cursor: 'pointer' }}
            >
              <OpenIcon />
            </Circle> : null}
            <Spacer />

            {/* Row Number Display */}
            {view === 'form' && (
              <>
                {loading ? (
                  <Text fontSize="sm" color="teal.500" pr={3} display="flex" alignItems="center">
                    Loading
                    <Box
                      as="span"
                      ml={1}
                      fontWeight="bold"
                      display="inline-block"
                      width="1em"
                      textAlign="left"
                      _after={{
                        content: '""',
                        animation: `${dots} 1.2s steps(3, end) infinite`,
                        display: 'inline-block',
                      }}
                    />
                  </Text>
                ) : (
                  <Text fontSize="sm" color="gray.600" pr={3}>
                    <Box as="span" fontWeight="semibold" color="gray.700">
                      Row:
                    </Box>{" "}
                    {
                      (() => {
                        const isNewRow = sheet?.data?.[selectedRowCol?.row]?.id == null || Object.keys(selectedRowCol).length === 0;
                        if (isNewRow) {
                          const nullIdIndex = sheet?.data?.findIndex(row => row.id === null);
                          let ans = nullIdIndex !== -1 ? nullIdIndex + 1 : sheet?.data?.length + 1;
                          return ans;
                        } else {
                          return selectedRowCol?.row + 1;
                        }
                      })()
                    }
                  </Text>
                )}
              </>
            )}

            <Button
              size="sm"
              colorScheme="teal"
              onClick={() => setView(view === 'file' ? 'form' : 'file')}
            >
              {view === 'file' ? 'Switch to Form' : 'Switch to File'}
            </Button>
            <CloseIcon
              style={{ cursor: 'pointer', height: '30px', width: '30px' }}
              onClick={onClose}
            />
          </HStack>
        </Box>
        {view === 'file' ? renderFile() : renderForm()}
      </ModalContent>
    </Modal>
  );
};

export default FormViewer;
